// @ts-strict-ignore
import { createFilterOptions } from '@material-ui/lab/Autocomplete';

import { Text } from '@/atoms/Text/Text';
import {
  NeedlAutocomplete,
  NeedlAutocompletePopper,
} from '@/components/NeedlAutocomplete';
import SearchIcon from '@/icons/search.svg?react';

import { useNoteSelectorContext } from './context';
import type { Option } from './types';
import { isCreatable, isNotebook } from './utils';

const filter = createFilterOptions<Option>();

export const Selector = ({ forceMount, portal }: SelectorType) => {
  const {
    disabled,
    id,
    open,
    anchorEl,
    disableAutocomplete,
    options,
    closeSelector,
    openState,
    onChange,
    primaryButtonHandler,
    notebooksSet,
    singleSelect,
  } = useNoteSelectorContext();

  if (disabled) {
    return null;
  }

  return (
    <NeedlAutocompletePopper<Option, true>
      id={id}
      portal={portal}
      open={open}
      forceMount={forceMount}
      anchorEl={anchorEl}
      placement='bottom-start'
      multiple
      disabled={disableAutocomplete}
      options={options}
      onClose={closeSelector}
      value={openState ?? []}
      placeholder='Find notebook or create one'
      endAdornment={<SearchIcon />}
      noOptionsText='Please create a Notebook'
      getOptionLabel={(option) => {
        if (isNotebook(option)) {
          return option.notebook_name;
        }
        if (isCreatable(option)) {
          return option.inputValue;
        }
        throw new Error('Unexpected option type');
      }}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);
        const input = params.inputValue.trim();

        if (input !== '' && !notebooksSet.has(input)) {
          filtered.push({
            inputValue: params.inputValue,
            title: (
              <>
                Create Notebook <b>&quot;{params.inputValue}&quot;</b>
              </>
            ),
          });
        }

        return filtered;
      }}
      renderOption={(option, { selected }) => {
        if (isNotebook(option)) {
          return (
            <>
              {singleSelect ? (
                <NeedlAutocomplete.Radio checked={selected} />
              ) : (
                <NeedlAutocomplete.Checkbox checked={selected} />
              )}
              <Text>{option.notebook_name}</Text>
            </>
          );
        }
        if (isCreatable(option)) {
          return (
            <>
              <NeedlAutocomplete.AddIcon />
              <Text>{option.title || option.inputValue}</Text>
            </>
          );
        }
        throw new Error('Unexpected option type');
      }}
      onChange={onChange}
      primaryButtonText='Done'
      primaryButtonHandler={primaryButtonHandler}
    />
  );
};

type SelectorType = {
  forceMount?: boolean;
  portal?: boolean;
};
